<template>
  <global-card
    :min-width="computedMinWidth"
    :max-width="maxWidth"
    :title="`${actionText} ${modelName}`"
    :heading-color="headingColor"
    :show-heading="withTitle"
    :closable="canClose"
    :without-text="!withText"
    with-actions
    @close="close"
  >
    <v-form>
      <slot />
    </v-form>
    <template #actions>
      <v-spacer />

      <v-btn
        v-if="canClose"
        color="dark-grey"
        :text="t('actions.close')"
        size="default"
        @click="close"
      />
      <slot name="actions" />

      <v-btn
        v-if="mode !== 'show'"
        color="primary"
        :disabled="actionInProgress"
        :loading="actionInProgress"
        size="default"
        :text="actionText"
        @click="save"
      />
    </template>
    <v-dialog
      v-model="localChecker"
      persistent
      width="600px"
    >
      <global-card :title="t('form.unsaved_changed')">
        <span class="text-font">
          {{ t('form.unsaved_changed_load') }}
        </span>
        <template #actions>
          <v-btn
            color="warning"
            :text="t('actions.discard')"
            size="default"
            @click="discardChanges()"
          />
          <v-btn
            color="primary"
            :text="t('actions.restore')"
            size="default"
            @click="loadModel(false)"
          />
        </template>
      </global-card>
    </v-dialog>
  </global-card>
</template>

<script setup lang="ts">
import type { AxiosError } from 'axios';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify/lib/framework.mjs';
import { useDataTableUtilities, useUploadLimit } from '@/composables';
import { useErrorStore } from '@/stores/errors';

type ModelEditMode = 'edit' | 'create' | 'show' | 'clone';

const props = withDefaults(defineProps<{
  baseUrl: string;
  modelName: string;
  modelId?: number | string;
  mode: ModelEditMode;
  modelUpdateUrl?: string;
  modelShowUrl?: string;
  modelCreationUrl?: string;
  includes?: Array<string>;
  fields?: Array<string>;
  minWidth?: string;
  maxWidth?: string;
  withTitle?: boolean;
  canClose?: boolean;
  saveWording?: string;
  headingColor?: string;
  withText?: boolean;
  loadWritable?: boolean;
  successMessage?: string;
}>(), {
  includes: () => [],
  fields: () => [],
  withTitle: () => true,
  canClose: () => true,
  headingColor: () => 'primary',
  withText: () => true,
  loadWritable: () => true,
  maxWidth: () => '1000px',
});

const emit = defineEmits(['model:updated', 'model:created', 'model:loaded', 'error:handled', 'save:failed', 'close']);

const { width, lgAndUp } = useDisplay();

const computedMinWidth = computed(() => {
  if (props.minWidth) {
    return props.minWidth;
  }

  if (lgAndUp.value) {
    return width.value * 0.5;
  }
  return width.value * 0.75;
});

const { t } = useI18n();
const { showSnackMessage } = useErrorStore();
const { appendFields, appendIncludes } = useDataTableUtilities();
const { exceedsRequestLimit } = useUploadLimit();
const item = defineModel<any>();
const errorStore = useErrorStore();
const { validationErrors } = storeToRefs(errorStore);

const localChecker = ref(false);
const updateUrl = computed(() => {
  if (props.modelUpdateUrl) {
    return props.modelUpdateUrl;
  }
  return `${props.baseUrl}/${props.modelId}`;
});

const cacheKey = computed(() => {
  return updateUrl.value;
});

const showUrl = computed<string>(() => {
  let url = `${props.baseUrl}/${props.modelId}`;
  if (props.modelShowUrl) {
    url = props.modelShowUrl;
  }
  if (props.includes.length > 0) {
    url = appendIncludes(url, props.includes);
  }

  if (props.fields.length > 0) {
    url = appendFields(url, props.fields);
  }

  return url;
});

const saveUrl = computed(() => {
  if (props.modelCreationUrl) {
    return props.modelCreationUrl;
  }
  return `${props.baseUrl}`;
});

const actionText = computed(() => {
  if (props.saveWording) {
    return props.saveWording;
  }
  return props.mode === 'edit' ? t('global.update') : t('actions.create');
});

function discardChanges() {
  localChecker.value = false;
  clearModelCache();
  loadModel();
}

function clearModelCache() {
  localStorage.removeItem(cacheKey.value);
}

// A draft holding newly added images can outgrow the browser's storage quota.
// Drop the older draft rather than restore it over the newer edits later on.
function setModelCache(model: unknown) {
  try {
    localStorage.setItem(cacheKey.value, JSON.stringify(model));
  }
  catch {
    clearModelCache();
  }
}

function getModelCache(parseJson = false) {
  if (parseJson) {
    return JSON.parse(localStorage.getItem(cacheKey.value) as string);
  }
  return localStorage.getItem(cacheKey.value);
}

async function loadModel(checkCached = true) {
  if (props.mode === 'create') {
    if (getModelCache()) {
      item.value = getModelCache(true);
    }
    emit('model:loaded');
    return;
  }

  try {
    const hasLocalCache = getModelCache();
    if (checkCached && hasLocalCache) {
      localChecker.value = true;
      return;
    }

    if (hasLocalCache) {
      item.value = getModelCache(true);
    }
    else {
      const response = await axios.get(showUrl.value);
      item.value = response.data.data;
    }
    localChecker.value = false;

    emit('model:loaded');
  }
  catch (error) {
    const errors = error as Error | AxiosError;
    if (axios.isAxiosError(errors)) {
      showSnackMessage(errors?.response?.data?.message);
    }
    else {
      showSnackMessage(t('errors.cannot_load'));
      console.error(error);
    }
  }
}

function close() {
  clearModelCache();
  emit('close');
}

// Constructing the error in this way may not be possible for all languages
// e.g. in Spanish the model name would need to be combined with el or la depending on the
// gender of the model name. In those languages the translator can decide to either return
// a phrase that at least includes the verb, or can just not translate the phrase at all and
// fall back to the generic error message
function buildErrorMessage(item: string, mode: ModelEditMode) {
  const action = t(`actions.${mode}`);
  return action ? t('errors.could_not_complete_action_with_item', { item, action }) : t('errors.failed_generic');
}

function isRequestTooLarge(error: AxiosError): boolean {
  return error.response?.status === 413;
}

const actionInProgress = ref(false);
async function save() {
  if (exceedsRequestLimit(item.value)) {
    showSnackMessage(t('errors.request_too_large'));
    emit('save:failed');
    return;
  }
  actionInProgress.value = true;
  try {
    if (props.modelId && props.mode !== 'clone') {
      await axios.put(updateUrl.value, item.value);
    }
    else {
      await axios.post(saveUrl.value, item.value);
    }
    clearModelCache();
  }
  catch (error) {
    actionInProgress.value = false;
    let errorMessage = buildErrorMessage(props.modelName, props.mode);
    if (axios.isAxiosError(error)) {
      errorMessage = error?.response?.data?.message || errorMessage;
      if (isRequestTooLarge(error)) {
        errorMessage = t('errors.request_too_large');
      }
      if (error.response?.status === 422) {
        validationErrors.value = error.response.data.errors;
        errorMessage = Object.values(error.response.data.errors)[0] as string;
      }
    }
    else {
      console.error(error);
    }
    showSnackMessage(errorMessage);
    emit('save:failed');
    return;
  }
  actionInProgress.value = false;
  validationErrors.value = {};
  switch (props.mode) {
    case 'create':
    case 'clone':
      emit('model:created');
      showSnackMessage(props.successMessage ?? t('global.success_created', { item: props.modelName }), 'success');
      break;
    case 'edit':
      emit('model:updated');
      showSnackMessage(props.successMessage ?? t('global.success_updated', { item: props.modelName }), 'success');
      break;
  }
  close();
}

const validate = debounce(async (field: string = '') => {
  if (item.value) {
    setModelCache(item.value);
  }
  if (exceedsRequestLimit(item.value)) {
    showSnackMessage(t('errors.request_too_large'));
    return;
  }
  const headers: Record<string, string | boolean> = { Precognition: true };
  if (field) {
    headers['Precogntion-Validate-Only'] = field;
  }
  try {
    if (props.modelId && props.mode !== 'clone') {
      await axios.put(updateUrl.value, item.value, { headers });
    }
    else {
      await axios.post(saveUrl.value, item.value, { headers });
    }
    validationErrors.value = {};
  }
  catch (error) {
    const errors = error as Error | AxiosError;
    if (axios.isAxiosError(errors)) {
      if (errors.response?.status === 422) {
        validationErrors.value = errors.response.data.errors;
      }
      if (isRequestTooLarge(errors)) {
        showSnackMessage(t('errors.request_too_large'));
      }
    }
  }
}, 1000);

// Validate (and keep the unsaved draft) on every change to the model,
// including the one loadModel() makes. Each form used to set up this watch
// itself; a form built on this component must not add its own.
watch(item, () => validate(), { deep: true });

onUnmounted(() => {
  validationErrors.value = {};
});
loadModel();
defineExpose({ save, validate });
</script>
